import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { FeesDto, ReadVariablesTunnelDto, SidebarComponentDto } from 'api/pricing';
import { fetchFunnels } from '../pricing/thunks/fetchFunnelsThunk';

// Constante pour la clé du composant sidebar
const SIDEBAR_COMPONENT_KEYS = {
  PRICING_VARIABLE_AND_FEES: 'PRICING_VARIABLE_AND_FEES',
} as const;

// Type pour la config des variables et frais reçue de l'API
interface ApiVariablesAndFeesConfig {
  variables?: ReadVariablesTunnelDto[];
  fees?: FeesDto[];
}

/**
 * Parse les composants sidebar pour extraire les variables et frais
 */
const parseSidebarVariablesAndFees = (sidebar: SidebarComponentDto[]): ApiVariablesAndFeesConfig | undefined => {
  for (const component of sidebar) {
    if (component.componentKey === SIDEBAR_COMPONENT_KEYS.PRICING_VARIABLE_AND_FEES) {
      return component.componentConfig as ApiVariablesAndFeesConfig;
    }
  }
  return undefined;
};

interface PricingValuesState {
  variables: ReadVariablesTunnelDto[]
  fees: FeesDto[]
  globalVariablesValues: Record<string, number>;
  globaleFeeValues: Record<string, number>;
  isUpdatingPrices: boolean;
  error: string | null;
}

const initialState: PricingValuesState = {
  variables: [],
  fees: [],
  globalVariablesValues: {},
  globaleFeeValues: {},
  isUpdatingPrices: false,
  error: null,
};

// Slice
const pricingValuesSlice = createSlice({
  name: 'pricingValues',
  initialState,
  reducers: {
    // Mettre à jour une variable spécifique
    updateGlobalVariableValue: (state, action: PayloadAction<{ id: string; value: number }>) => {
      state.globalVariablesValues[action.payload.id] = action.payload.value;
      state.error = null;
    },

    // Mettre à jour une fee spécifique
    updateGlobalFeeValue: (state, action: PayloadAction<{ itemId: string; value: number }>) => {
      state.globaleFeeValues[action.payload.itemId] = action.payload.value;
      state.error = null;
    },

    // Reset des erreurs
    clearError: (state) => {
      state.error = null;
    },

    // Reset complet des valeurs
    resetValues: (state) => {
      state.globalVariablesValues = {};
      state.globaleFeeValues = {};
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchFunnels.fulfilled, (state, action) => {
        // Nouvelle structure plate: sidebar est directement à la racine
        const sidebar = action.payload.sidebar;
        const variablesAndFees = sidebar ? parseSidebarVariablesAndFees(sidebar) : undefined;

        if (variablesAndFees) {
          state.variables = variablesAndFees.variables || []
          state.fees = variablesAndFees.fees || []
          if (!state.globalVariablesValues) {
            state.globalVariablesValues = {};
          }
          if (!state.globaleFeeValues) {
            state.globaleFeeValues = {};
          }
          state.variables.forEach(({ variableRefId, value }) => {
            state.globalVariablesValues[variableRefId] = value;
          });
          state.fees.forEach(({ code, value }) => {
            state.globaleFeeValues[code] = value;
          });
        }
      }
      )

  },
});

// Actions
export const {
  updateGlobalVariableValue,
  updateGlobalFeeValue,
  clearError,
  resetValues,
} = pricingValuesSlice.actions;

export default pricingValuesSlice.reducer;
